import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MInputField from '@/components/ui/atoms/MInputField';
import MDateInputField from '@/components/ui/molecules/MDateInputField';
import MTextArea from '@/components/ui/atoms/MTextArea';
import { MButton } from '@/components/ui/atoms/MButtons';
import FormHeader from '@/components/ui/organisms/MHeader';
import useHealthMemory from '@/hooks/useHealthMemory';
import { getMemberInfo } from '@/hooks/useMembers';
import MSpacer from '@/components/ui/atoms/MSpacer';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

// Placeholder type and hooks for demonstration
interface HealthMemoryFormData {
  title: string;
  year: string;
  details: string;
}

const HealthMemoryForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const { createHealthMemory, updateHealthMemory, healthMemories, fetchHealthMemory } = useHealthMemory(memberId);

  const params = new URLSearchParams(location.search);
  const mode = params.get('mode');
  const id = params.get('id');
  const yearParam = params.get('year');

  // In a real implementation, fetch the memory by id for edit mode
  const [form, setForm] = useState<HealthMemoryFormData>({
    title: '',
    year: yearParam || '',
    details: '',
  });
  const [initialForm, setInitialForm] = useState<HealthMemoryFormData | null>(null);
  const [loading, setLoading] = useState(false);

  // Prefill form in edit mode (fetch from backend)
  useEffect(() => {
    if (mode === 'edit' && id) {
      fetchHealthMemory(id).then(memory => {
        if (memory) {
          const fetched = {
            title: memory.title || '',
            year: memory.year ? memory.year.toString() : '',
            details: memory.details || '',
          };
          setForm(fetched);
          setInitialForm(fetched);
        }
      });
    } else {
      // Track page load for add mode
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
        screenName: ANALYTICS_SCREEN_NAMES.HEALTH_MEMORY_ADD,
      });
    }
  }, [mode, id]);

  const isFormValid = form.title.trim().length > 0 && form.year.length === 4;
  const isDirty = mode !== 'edit' || !initialForm || (
    form.title !== initialForm.title ||
    form.year !== initialForm.year ||
    form.details !== initialForm.details
  );

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Track done click
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
        screenName: ANALYTICS_SCREEN_NAMES.HEALTH_MEMORY_ADD_CONFIRM,
      });
      
      if (mode === 'edit' && id) {
        await updateHealthMemory(id, {
          title: form.title,
          year: Number(form.year),
          details: form.details,
        });
      } else {
        await createHealthMemory({
          title: form.title,
          year: Number(form.year),
          details: form.details,
          memberId,
        });
      }
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-page-root flex flex-col" style={{ minHeight: 'calc(var(--vh, 1vh) * 100 - 128px)' }}>
      <FormHeader
        module="Health memory notes"
        memberInfo={memberInfo}
        title='Your memory note'
        description="Note an injury, condition, or episode you recall that give caregivers the full picture."
        hideLearnMore
      />
      <MSpacer mt={24} />
      <div className="flex flex-col gap-[32px]">
        <MInputField
          label="What was the event?"
          value={form.title}
          placeholder="e.g. Asthma episode, knee injury"
          size="md"
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
        />
        <MDateInputField
          showYearOnly
          label="When did this happen?"
          minYear={memberInfo.dobYear || 1940}
          value={form.year ? new Date(form.year) : undefined}
          onDateChange={date => setForm(f => ({ ...f, year: date.getFullYear().toString() }))}
          formatDate={date => date ? date.getFullYear().toString() : ''}
          placeholder="Not Answered"
          size="md"
          className="add-chip-input"
          datePickerTitle="Select year"
        />
        <MTextArea
          label="Add details (optional)"
          value={form.details}
          placeholder="Add symptoms, notes, or treatment details"
          className='h-[135px]'
          maxLength={1000}
          onChange={e => setForm(f => ({ ...f, details: e.target.value }))}
        />
      </div>
      <div className="confirm-footer mt-8">
        <MButton
          type="button"
          variant="filled"
          size="lg"
          className="w-full"
          disabled={!isFormValid || loading || !isDirty}
          loading={loading}
          onClick={handleSubmit}
        >
          Done
        </MButton>
      </div>
    </div>
  );
};

export default HealthMemoryForm;
